import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Button } from "@/components/ui/button";
import { CheckCircle, X, Spinner } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VollmachtSign() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const canvasRef = useRef(null);
  const [hasSig, setHasSig] = useState(false);
  const [drawing, setDrawing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/vollmacht/sign/${token}/info`);
        setInfo(r.data);
        if (r.data.kanzlei?.has_logo) {
          setLogoUrl(`${BACKEND_URL}/api/vollmacht/sign/${token}/logo`);
        }
      } catch (e) {
        setError(e?.response?.data?.detail || "Link ungültig oder abgelaufen");
      } finally { setLoading(false); }
    })();
  }, [token]);

  const getPos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches?.[0];
    const x = (t?.clientX ?? e.clientX) - r.left;
    const y = (t?.clientY ?? e.clientY) - r.top;
    return { x: x * (c.width / r.width), y: y * (c.height / r.height) };
  };
  const start = (e) => {
    e.preventDefault();
    setDrawing(true);
    const ctx = canvasRef.current.getContext("2d");
    const p = getPos(e);
    ctx.beginPath(); ctx.moveTo(p.x, p.y);
    ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.strokeStyle = "#0A192F";
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = getPos(e); ctx.lineTo(p.x, p.y); ctx.stroke();
    setHasSig(true);
  };
  const end = () => setDrawing(false);
  const clear = () => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHasSig(false);
  };

  const submit = async () => {
    if (!hasSig) { toast.error("Bitte unterschreiben"); return; }
    setSubmitting(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      await axios.post(`${BACKEND_URL}/api/vollmacht/sign/${token}`, { signature_data_url: dataUrl });
      setDone(true);
      toast.success("Unterschrift übermittelt — vielen Dank!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Übermitteln");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-[#6B7280]">
          <Spinner size={32} className="animate-spin" />
          <p>Lade Vollmacht …</p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4">
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 max-w-md text-center" data-testid="sign-error">
          <X size={36} className="text-[#D32F2F] mx-auto mb-3" weight="bold" />
          <h2 className="font-serif text-2xl mb-2">Link ungültig</h2>
          <p className="text-sm text-[#6B7280]">{error}</p>
          <p className="text-xs text-[#9CA3AF] mt-4">Bitte wenden Sie sich an Ihre Kanzlei.</p>
        </div>
      </div>
    );
  }
  if (done) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4">
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 max-w-md text-center" data-testid="sign-done">
          <CheckCircle size={48} className="text-[#16A34A] mx-auto mb-3" weight="duotone" />
          <h2 className="font-serif text-2xl mb-2">Vielen Dank!</h2>
          <p className="text-sm text-[#6B7280]">Ihre Unterschrift wurde sicher übermittelt. {info?.kanzlei?.name || "Ihre Kanzlei"} wurde informiert.</p>
        </div>
      </div>
    );
  }

  const m = info.mandant || {};
  const k = info.kanzlei || {};
  const kanzleiAdr = [k.adresse, [k.plz, k.ort].filter(Boolean).join(" ")].filter(Boolean).join(" · ");
  return (
    <div className="min-h-screen bg-[#FAFAFA] py-12 px-4" data-testid="vollmacht-sign-page">
      <Toaster position="top-right" richColors />
      <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-sm border border-[#E5E7EB] p-8 md:p-12">

        {/* Briefkopf — Logo + Kanzlei-Daten links, Aktenzeichen rechts */}
        <div className="flex items-start justify-between gap-6 pb-6 border-b border-[#E5E7EB] mb-8 flex-wrap">
          <div className="flex items-center gap-5 min-w-0">
            {logoUrl && (
              <img
                src={logoUrl}
                alt={k.name || "Kanzlei"}
                className="h-16 max-w-[180px] object-contain"
                onError={() => setLogoUrl(null)}
              />
            )}
            <div className="min-w-0">
              <p className="font-serif text-lg text-[#0A192F] tracking-wide">{k.name || "Kanzlei"}</p>
              {k.anwalt && <p className="text-xs text-[#6B7280] mt-0.5">{k.anwalt}</p>}
              {kanzleiAdr && <p className="text-xs text-[#6B7280] mt-1">{kanzleiAdr}</p>}
              {k.telefon && <p className="text-xs text-[#6B7280] mt-0.5">Tel.: {k.telefon}</p>}
              {k.email && <p className="text-xs text-[#6B7280]">{k.email}</p>}
            </div>
          </div>
          {info.aktenzeichen && (
            <div className="border border-[#1A2238] rounded px-3 py-2 bg-[#FAFAFA] text-right">
              <p className="overline text-[#6B7280]">Aktenzeichen</p>
              <p className="font-serif font-bold text-[#0A192F] text-base mt-0.5">{info.aktenzeichen}</p>
            </div>
          )}
        </div>

        <div className="text-center mb-8">
          <p className="overline text-[#D32F2F] mb-2">Online-Vollmacht</p>
          <h1 className="font-serif text-3xl">Vollmacht erteilen</h1>
        </div>

        <div className="border border-[#E5E7EB] rounded-lg p-6 mb-6 bg-[#FAFAFA]">
          <p className="overline text-[#9CA3AF] mb-3">Vollmachtgeber</p>
          <p className="font-medium text-lg">{m.vorname} {m.nachname}</p>
          {m.strasse && <p className="text-sm text-[#4B5563]">{m.strasse}</p>}
          {(m.plz || m.ort) && <p className="text-sm text-[#4B5563]">{m.plz} {m.ort}</p>}
        </div>

        {info.vollmacht_text && (
          <div className="mb-6">
            <p className="overline text-[#9CA3AF] mb-3">Vollmachtstext</p>
            <p className="text-sm text-[#4B5563] whitespace-pre-line leading-relaxed">{info.vollmacht_text}</p>
          </div>
        )}

        <div className="mb-6">
          <p className="overline text-[#D32F2F] mb-3">Ihre Unterschrift</p>
          <p className="text-xs text-[#6B7280] mb-2">Bitte mit der Maus, dem Trackpad oder dem Finger im Feld unten unterschreiben.</p>
          <canvas
            ref={canvasRef}
            width={800}
            height={250}
            className="w-full border-2 border-dashed border-[#9CA3AF] bg-white rounded touch-none cursor-crosshair"
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
            data-testid="sign-canvas"
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={clear} data-testid="sign-clear">
            <X size={14} className="mr-1" /> Neu zeichnen
          </Button>
          <Button
            onClick={submit}
            disabled={!hasSig || submitting}
            className="bg-[#1A2238] hover:bg-[#2C3859]"
            data-testid="sign-submit"
          >
            {submitting ? <Spinner size={14} className="mr-2 animate-spin" /> : <CheckCircle size={14} className="mr-2" weight="duotone" />}
            Unterschrift bestätigen
          </Button>
        </div>

        <p className="text-xs text-[#9CA3AF] text-center mt-8">
          Sicher übermittelt · Link 14 Tage gültig · {k.name}
        </p>
      </div>
    </div>
  );
}
